import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

export default function WhatWeDo() {
  const capabilities = [
    {
      icon: 'ri-chat-check-line',
      title: 'Resolve First-Line Inquiries',
      description: 'Handle repetitive questions instantly without human involvement.'
    },
    {
      icon: 'ri-database-2-line',
      title: 'Access Internal Systems',
      description: 'Retrieve account, billing, or order data from your existing tools.'
    },
    {
      icon: 'ri-git-branch-line',
      title: 'Follow Escalation Logic',
      description: 'Route complex cases to human agents with full context attached.'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Reduce Ticket Volume',
      description: 'Decrease inbound tickets and lower operational cost per resolution.'
    }
  ];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, getItemStyle } = useStaggerAnimation(capabilities.length, { threshold: 0.1 });

  return (
    <section className="bg-white py-16 md:py-28">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[45%_55%] gap-10 lg:gap-16 items-start">
          <div
            ref={headerRef}
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(32px)',
              transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B8985F]/10 mb-5 md:mb-6">
              <span className="text-[12px] tracking-[1.5px] uppercase text-[#B8985F] font-semibold">What We Do</span>
            </div>
            <h2 className="text-[28px] md:text-[38px] font-bold text-[#1A1A1A] leading-[1.3] mb-4 md:mb-6">
              AI support agents that integrate into your workflow
            </h2>
            <p className="text-[15px] md:text-[17px] text-[#666] leading-[1.7] mb-6 md:mb-8">
              EasyMarketPro designs and deploys AI support agents that integrate into your existing support infrastructure and handle high-volume, repetitive inquiries with precision.
            </p>
            <div className="relative w-full h-[200px] md:h-[280px] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20customer%20support%20dashboard%20interface%20on%20computer%20screen%20showing%20ticket%20management%20system%20with%20analytics%20charts%20and%20AI%20automation%20indicators%2C%20warm%20amber%20gold%20accent%20colors%20on%20dark%20interface%2C%20clean%20minimal%20enterprise%20software%20design%2C%20professional%20workspace%20setting%20with%20soft%20lighting%2C%20technology%20operations%20center%20aesthetic&width=520&height=280&seq=whatwedo-dashboard-visual-01&orientation=landscape"
                alt="AI Support Dashboard"
                className="w-full h-full object-cover object-top rounded-lg"
              />
            </div>
          </div>

          <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {capabilities.map((cap, index) => (
              <div
                key={index}
                className="bg-[#FAFAF8] rounded-lg p-5 md:p-6 border border-[#F0EDE6] hover:border-[#B8985F]/30 hover:shadow-[0_8px_30px_rgba(184,152,95,0.08)] transition-all duration-300 cursor-pointer group"
                style={getItemStyle(index)}
              >
                <div className="w-11 h-11 rounded-lg bg-[#B8985F]/10 flex items-center justify-center mb-4 group-hover:bg-[#B8985F]/20 transition-colors">
                  <i className={`${cap.icon} text-[#B8985F] text-lg`}></i>
                </div>
                <h3 className="text-[15px] md:text-[16px] font-semibold text-[#1A1A1A] mb-2">{cap.title}</h3>
                <p className="text-[13px] md:text-[14px] text-[#888] leading-[1.6]">{cap.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
